import { Col, FormGroup, Row, Text } from '@ksyos/design-system';
import type { IOorkV1ProcedureCustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/oorkV1ProcedureCustomControlData';
import type { ValidationErrors } from '@frontend/utils/index';
import { StyledBorderAndPadding } from '@frontend/components/elements/common';
import { OorkV1ReadOrEditInputField } from './OorkV1ProcedureReadOrEditInputField';

export type IOorkV1ProcedureOdOsOdsFindingsRowProps =
    | IOorkV1ProcedureVisusFieldSetRowAddEditProps
    | IOorkV1ProcedureVisusFieldSetRowReadProps;
export interface IOorkV1ProcedureVisusFieldSetRowAddEditProps extends IOorkV1ProcedureVisusFieldSetRowReadProps {
    errors?: ValidationErrors;
    field: string;
    handleChangeCustomControl?: <T = IOorkV1ProcedureCustomControlData>(values: Partial<T>) => Promise<void>;
    isRequired?: boolean;
    label: string;
    validateOneCustomControl?: (value: keyof IOorkV1ProcedureCustomControlData) => Promise<void>;
    value_od?: string | null;
    value_ods?: string | null;
    value_os?: string | null;
}

export interface IOorkV1ProcedureVisusFieldSetRowReadProps {
    isReadOnly: boolean;
}

export function OorkV1ProcedureVisusFieldSetRow(props: IOorkV1ProcedureVisusFieldSetRowAddEditProps) {
    const { errors, field, handleChangeCustomControl, isReadOnly, isRequired, label, value_od, value_os, value_ods } =
        props;

    const hasErrors =
        props.errors?.has(`customControlData.${field}_od`) ||
        errors?.has(`customControlData.${field}_ods`) ||
        errors?.has(`customControlData.${field}_os`);

    return (
        <FormGroup
            hasErrors={hasErrors}
            isRequired={isRequired}
            label={label}
        >
            <Row columnGap={[2, 4, 6]}>
                <Col
                    span={[12, 6, null, null, 4]}
                    style={{ minWidth: 240 }}
                >
                    <StyledBorderAndPadding>
                        <Text
                            fontWeight="bold"
                            pb={2}
                            size="xs"
                        >
                            OD
                        </Text>
                        <OorkV1ReadOrEditInputField
                            error={errors?.get(`customControlData.${field}_od`)}
                            isReadOnly={isReadOnly}
                            onChange={createOnChange(`${field}_od`)}
                            onDoValidate={async () =>
                                await props.validateOneCustomControl?.(
                                    `${field}_od` as keyof IOorkV1ProcedureCustomControlData
                                )
                            }
                            value={value_od || null}
                        />
                    </StyledBorderAndPadding>
                </Col>
                <Col
                    span={[12, 6, null, null, 4]}
                    style={{ minWidth: 240 }}
                >
                    <StyledBorderAndPadding>
                        <Text
                            fontWeight="bold"
                            pb={2}
                            size="xs"
                        >
                            OS
                        </Text>
                        <OorkV1ReadOrEditInputField
                            error={errors?.get(`customControlData.${field}_os`)}
                            isReadOnly={isReadOnly}
                            onChange={createOnChange(`${field}_os`)}
                            onDoValidate={async () =>
                                await props.validateOneCustomControl?.(
                                    `${field}_os` as keyof IOorkV1ProcedureCustomControlData
                                )
                            }
                            value={value_os || null}
                        />
                    </StyledBorderAndPadding>
                </Col>
                <Col
                    span={[12, 6, null, null, 4]}
                    style={{ minWidth: 240 }}
                >
                    <StyledBorderAndPadding>
                        <Text
                            fontWeight="bold"
                            pb={2}
                            size="xs"
                        >
                            ODS
                        </Text>
                        <OorkV1ReadOrEditInputField
                            error={errors?.get(`customControlData.${field}_ods`)}
                            isReadOnly={isReadOnly}
                            onChange={createOnChange(`${field}_ods`)}
                            onDoValidate={async () =>
                                await props.validateOneCustomControl?.(
                                    `${field}_ods` as keyof IOorkV1ProcedureCustomControlData
                                )
                            }
                            value={value_ods || null}
                        />
                    </StyledBorderAndPadding>
                </Col>
            </Row>
        </FormGroup>
    );

    function createOnChange(key: string) {
        return async (value: string | null) => {
            await handleChangeCustomControl?.({ [key]: value && value.trimStart() });
        };
    }
}
